// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { convertApiRoleToUiRole } from '../utils/roleUtils';
import { useNavigate } from "react-router";


interface SuspensionInfo {
  suspendu: boolean;
  motif?: string;
  depuis?: string;
}

interface User {
  id: string | number;
  name: string;
  email: string;
  role: string; // Utiliser string au lieu d'un type union restrictif pour supporter les rôles convertis
  token: string;
  suspension?: SuspensionInfo | null; // FRONT-19: Données de suspension
  entiteId?: number; // Identifiant de l'entité prestataire (EPIC-8: estMandataire check)
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean; // Ajout de isAuthenticated à l'interface
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  suspensionInfo: SuspensionInfo | null; // FRONT-19: Info suspension exposée
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false); // État pour suivre l'authentification
  const [isLoading, setIsLoading] = useState(true);
  const [suspensionInfo, setSuspensionInfo] = useState<SuspensionInfo | null>(null); // FRONT-19
  let navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
        // FRONT-19: Restaurer les données de suspension depuis le user stocké
        if (parsedUser.suspension?.suspendu) {
          setSuspensionInfo(parsedUser.suspension);
        }
      } catch (error) {
        console.error('Erreur lors de la lecture des données utilisateur:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }

    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });

      console.log('route:', process.env.REACT_APP_API_URL)

      const data = await response.json();

      // Gérer les erreurs HTTP (403, 401, etc.)
      if (!response.ok) {
        throw new Error(data.message || 'Email ou mot de passe incorrect');
      }

      if (data.status === 'success') {
        // L'API retourne "name" et "roles" (tableau)
        const apiUser = data.user;

        // Créer l'objet utilisateur à partir des données de l'API
        const userData = {
          id: apiUser.id,
          name: apiUser.nom || apiUser.name || '',
          email: apiUser.email,
          role: apiUser.role || (apiUser.roles && apiUser.roles[0]) || 'acheteur',
          token: data.token,
          suspension: data.suspension || null, // FRONT-19: Données de suspension
          entiteId: apiUser.id_entite ?? undefined, // MS messagerie : id de l'entité prestataire
        };

        // Convertir le rôle API en rôle UI pour l'affichage
        const userWithConvertedRole = {
          ...userData,
          role: convertApiRoleToUiRole(userData.role)
        };

        // Sauvegarde en localStorage et mise à jour des états
        localStorage.setItem('user', JSON.stringify(userWithConvertedRole));
        localStorage.setItem('token', data.token);

        // Mise à jour des états React
        setUser(userWithConvertedRole);
        setIsAuthenticated(true);

        // FRONT-19: Mettre à jour l'état de suspension
        if (data.suspension?.suspendu) {
          setSuspensionInfo(data.suspension);
        } else {
          setSuspensionInfo(null);
        }
      } else {
        throw new Error(data.message || 'Email ou mot de passe incorrect');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false); // Mettre à jour l'état d'authentification
    setSuspensionInfo(null); // FRONT-19: Reset de la suspension
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.scrollTo(0, 0);
    navigate('/');
  };

  // FRONT-19: Vérification périodique du statut de suspension (toutes les 5 minutes)
  useEffect(() => {
    if (!isAuthenticated || !user?.token) return;

    const checkSuspension = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/suspension/status`, {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) return; // Silencieux en cas d'erreur

        const data = await response.json();
        if (data.suspendu) {
          setSuspensionInfo({
            suspendu: true,
            motif: data.motif,
            depuis: data.depuis,
          });
        } else {
          setSuspensionInfo(null);
        }
      } catch (e) {
        // Silencieux - ne pas bloquer la navigation pour une erreur réseau
      }
    };

    const interval = setInterval(checkSuspension, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, [isAuthenticated, user?.token]);

  const value = {
    user,
    isAuthenticated, // Exposer isAuthenticated dans le contexte
    login,
    logout,
    isLoading,
    suspensionInfo, // FRONT-19: Info suspension
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;